import React, {ReactNode, useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {Form} from 'react-bootstrap';
import {SelectField, MultiSelectField, SelectOption as SelectFieldOption} from '@/components/forms';
import {createFilter} from 'react-select';
import {Tooltip} from '@/components/tooltips';
import {AggregationType, BarOrientation, ChartOutput, ChartQuery, ChartSettings} from 'entities/chart';
import {FormikT} from 'utils/types';
import {
    aggregationChoicesFilters,
    chartMetricTimelineTypes,
    chartOutputTypeChoicesWithLabels,
    ChartType,
    deprecatedChartTypes,
    EventFilter,
    hierarchicalAggregationChartTypes,
    numericMetricTimelineTypes,
    pivotValueTypes,
} from '@/views/storyboard/utils/chartConstants';
import {applyOutputChange} from '@/views/storyboard/utils/applyChartTypeChange';
import {
    showsMetricAggregationsTypeField,
    showsMetricIntervalField,
} from '@/views/storyboard/utils/sanitizeChartQuery';
import {ChartAggregationsSelect} from '@/views/storyboard/form_fields/ChartAggregationsSelect';
import ChartAggregationsMultiSelect from '@/views/storyboard/form_fields/ChartAggregationsMultiSelect';
import ChartPivotColumnSelect from '@/views/storyboard/form_fields/ChartPivotColumnSelect';
import ChartSettingsPanel from '@/views/storyboard/chart_settings/ChartSettingsPanel';
import ChartTypeIconSelector from '@/views/storyboard/chart_settings/ChartTypeIconSelector';
import {ChartAggregationsSelectCross} from '@/views/storyboard/form_fields/ChartAggregationsSelectCross';
import {resolveRestrictedFields} from '@/utils/fieldRegistry';
import {InlineAlert} from '@/components/alerts';
import ChartSettingsCompatibilityHintsPanel from '@/views/storyboard/chart_settings/ChartSettingsCompatibilityHintsPanel';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';

const selectStyles = {
    control: (base: any) => ({...base, minHeight: 34, height: 34}),
    valueContainer: (base: any) => ({...base, padding: '0 8px'}),
    indicatorsContainer: (base: any) => ({...base, height: 34}),
    menu: (base: any) => ({...base, marginBottom: 8}),
    menuList: (base: any) => ({...base, maxHeight: 180}),
    option: (base: any, state: any) => ({
        ...base,
        background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
        backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        ':active': {
            ...base[':active'],
            backgroundColor: 'rgba(0, 158, 251, 0.08)',
        },
    }),
};

const aggregationMenuProps = {
    maxMenuHeight: 180,
    menuPlacement: 'auto' as const,
    menuShouldScrollIntoView: true,
};

const chartOutputGroups = [
    {
        labelKey: 'chart_settings_output_group_over_time.text',
        outputs: [
            ChartOutput.TimelineByDate,
            ChartOutput.TermsTimeline,
            ChartOutput.MultiTermsTimeline,
            ChartOutput.MetricTimeline,
        ],
    },
    {
        labelKey: 'chart_settings_output_group_by_category.text',
        outputs: [
            ChartOutput.Terms,
            ChartOutput.MultiTerms,
            ChartOutput.TermsMetric,
        ],
    },
    {
        labelKey: 'chart_settings_output_group_table_matrix.text',
        outputs: [
            ChartOutput.PivotTerms,
        ],
    },
];

const chartOutputDescriptionKeys: Partial<Record<ChartOutput, string>> = {
    [ChartOutput.TimelineByDate]: 'chart_settings_output_timeline_by_date_description.text',
    [ChartOutput.TermsTimeline]: 'chart_settings_output_terms_timeline_description.text',
    [ChartOutput.MultiTermsTimeline]: 'chart_settings_output_multi_terms_timeline_description.text',
    [ChartOutput.MetricTimeline]: 'chart_settings_output_metric_timeline_description.text',
    [ChartOutput.Terms]: 'chart_settings_output_terms_description.text',
    [ChartOutput.MultiTerms]: 'chart_settings_output_multi_terms_description.text',
    [ChartOutput.TermsMetric]: 'chart_settings_output_terms_metric_description.text',
    [ChartOutput.PivotTerms]: 'chart_settings_output_pivot_terms_description.text',
};

const groupOutputOptions = (
    outputChoices: [ChartOutput, string][] | undefined,
) => {
    if (!outputChoices?.length) {
        return [];
    }

    const choiceMap = new Map(outputChoices.map(([value, label]) => [value, label]));

    return chartOutputGroups
        .map((group) => ({
            label: i18n.t(group.labelKey),
            options: group.outputs
                .filter((output) => choiceMap.has(output))
                .map((output): SelectFieldOption => ({
                    value: output,
                    label: choiceMap.get(output) || '',
                    description: chartOutputDescriptionKeys[output]
                        ? i18n.t(chartOutputDescriptionKeys[output]!)
                        : '',
                })),
        }))
        .filter((group) => group.options.length > 0);
};

const OUTPUT_TYPE_OPTION_HEIGHT = 52;
const OUTPUT_TYPE_GROUP_HEADER_HEIGHT = 28;
const OUTPUT_TYPE_MENU_PADDING = 16;

const getOutputTypeMaxMenuHeight = (
    outputChoices: [ChartOutput, string][] | undefined,
): number => {
    const groups = groupOutputOptions(outputChoices);
    const optionCount = groups.reduce((sum, group) => sum + group.options.length, 0);

    return optionCount * OUTPUT_TYPE_OPTION_HEIGHT
        + groups.length * OUTPUT_TYPE_GROUP_HEADER_HEIGHT
        + OUTPUT_TYPE_MENU_PADDING;
};

const formatOutputOptionLabel = (option: SelectFieldOption, meta: any) => {
    if (meta.context === 'value') {
        return option.label;
    }

    return (
        <div className="cs-filter-dropdown__option">
            <span className="cs-filter-dropdown__option-row">
                <span className="cs-filter-dropdown__option-name">
                    {option.label}
                </span>
                {option.description ? (
                    <span className="cs-filter-dropdown__option-desc">
                        {option.description}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

const createOutputTypeSelectStyles = (maxMenuHeight: number) => ({
    control: selectStyles.control,
    valueContainer: selectStyles.valueContainer,
    indicatorsContainer: selectStyles.indicatorsContainer,
    menu: selectStyles.menu,
    menuList: (base: any) => ({
        ...base,
        maxHeight: maxMenuHeight,
        padding: '0 6px 6px',
    }),
    group: (base: any) => ({...base, paddingBottom: 0, paddingTop: 2}),
    groupHeading: (base: any) => ({...base, marginBottom: 0, padding: 0}),
    option: (base: any, state: any) => ({
        ...base,
        padding: 0,
        background: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
        backgroundColor: state.isFocused ? 'rgba(0, 158, 251, 0.08)' : 'transparent',
        color: 'inherit',
        cursor: 'pointer',
        ':active': {
            ...base[':active'],
            backgroundColor: 'rgba(0, 158, 251, 0.08)',
        },
    }),
});

type WordCloudFilteringList = [number, string];

interface UserDataSlice {
    restrictedFieldKeys?: string[];
}

interface Props {
    chartQueryFormik: FormikT<ChartQuery>;
    chartSettingsFormik: FormikT<ChartSettings>;
    eventFilters: EventFilter[];
    eventFiltersLoading: boolean;
    userData: UserDataSlice | undefined;
    wordcloudFilteringListsChoices: WordCloudFilteringList[] | undefined;
    onChartTypeChange: (nextType: ChartType, nextBarOrientation?: BarOrientation) => void;
}

const chartQueryWithoutSearchText = (query: ChartQuery): Omit<ChartQuery, 'query_string'> => {
    const {query_string: _queryString, ...rest} = query;
    return rest;
};

const chartSettingsWithoutPreviewText = (
    settings: ChartSettings,
): Omit<ChartSettings, 'title' | 'description'> => {
    const {title: _title, description: _description, ...rest} = settings;
    return rest;
};

const ChartSettingsVisualizationPanel = React.memo<Props>(({
    chartQueryFormik,
    chartSettingsFormik,
    eventFilters,
    eventFiltersLoading,
    userData,
    wordcloudFilteringListsChoices,
    onChartTypeChange,
}) => {
    const chartSettingsType = chartSettingsFormik.values.type;
    const outputTypeMaxMenuHeight = useMemo(
        () => getOutputTypeMaxMenuHeight(chartOutputTypeChoicesWithLabels[chartSettingsType]),
        [chartSettingsType],
    );
    const outputTypeSelectStyles = useMemo(
        () => createOutputTypeSelectStyles(outputTypeMaxMenuHeight),
        [outputTypeMaxMenuHeight],
    );
    const isDeprecatedChartType = deprecatedChartTypes.includes(chartSettingsType);
    const deprecatedChartTypeLabel = chartSettingsType === ChartType.Concordance
        ? i18n.t('concordance.text')
        : i18n.t('trending_words.text');

    const openLexiconsView = () => {
        window.open(window.URLS.adminDictionaries, '_blank');
    };

    const aggregationFieldProps = {
        eventFilters,
        eventFiltersLoading,
    };

    return (
        <div className="chart-settings-form-aggregation-section">
                        <div className="chart-settings-form-section__title chart-settings-form-section__title--numbered">
                            <span className="chart-settings-form-section__number">2</span>
                            {i18n.t('chart_settings_section_visualization.text')}
                        </div>
                        <ChartSettingsPanel className="chart-settings-chart-type-panel">
                            {isDeprecatedChartType ? (
                                <InlineAlert
                                    variant="warning"
                                    icon="mdi-information-outline"
                                    message={i18n.t('chart_deprecated_warning.text', {chartType: deprecatedChartTypeLabel})}
                                    className="chart-settings-deprecation-warning"
                                />
                            ) : null}
                            <ChartTypeIconSelector
                                value={chartSettingsFormik.values.type}
                                barOrientation={chartSettingsFormik.values.barOrientation}
                                onChange={onChartTypeChange}
                                error={chartSettingsFormik.errors.type}
                                touched={chartSettingsFormik.touched.type}
                            />
                        </ChartSettingsPanel>
                        <ChartSettingsPanel>
                    {chartOutputTypeChoicesWithLabels[chartSettingsFormik.values.type]?.length
                        ? (
                            <Form.Group className="cs-field" controlId="outputType" >
                                <label className="cs-field__label">{i18n.t('jt_output_type.text')}</label>
                                <SelectField
                                    compact
                                    name="output"
                                    value={chartOutputTypeChoicesWithLabels[chartSettingsFormik.values.type]?.find(el => el[0] === chartQueryFormik.values.output) ? {
                                        value: chartQueryFormik.values.output,
                                        label: chartOutputTypeChoicesWithLabels[chartSettingsFormik.values.type]?.find(el => el[0] === chartQueryFormik.values.output)?.[1] || ''
                                    } : null}
                                    onChange={(v) => {
                                        const nextOutput: ChartOutput | undefined =
                                            v?.value || chartOutputTypeChoicesWithLabels[chartSettingsFormik.values.type]?.[0][0];

                                        if (!nextOutput) {
                                            return;
                                        }

                                        const {chartQuery, chartSettings} = applyOutputChange(
                                            chartSettingsFormik.values.type,
                                            nextOutput,
                                            chartQueryFormik.values,
                                            chartSettingsFormik.values,
                                            eventFilters,
                                        );

                                        if ([
                                            ChartOutput.Terms,
                                            ChartOutput.TermsTimeline,
                                            ChartOutput.MetricTimeline,
                                            ChartOutput.TermsMetric,
                                        ].includes(nextOutput)) {
                                            const restrictedFields = resolveRestrictedFields(userData?.restrictedFieldKeys);
                                            const availableOptions = aggregationChoicesFilters[nextOutput](eventFilters)
                                                .filter((el) => el[4] === true)
                                                .filter((el) => {
                                                    if (!restrictedFields.length) return true;
                                                    const fieldName = el[0].replace('all_fields.', '').replace('.keyword', '');
                                                    return !restrictedFields.includes(fieldName);
                                                });

                                            chartQuery.aggs_item = availableOptions[0]?.[0] ?? '';
                                        } else {
                                            chartQuery.aggs_item = '';
                                        }

                                        chartQueryFormik.setValues(chartQuery, false);
                                        chartSettingsFormik.setValues(chartSettings, false);
                                    }}
                                    options={groupOutputOptions(chartOutputTypeChoicesWithLabels[chartSettingsFormik.values.type])}
                                    isRequired={false}
                                    error={chartQueryFormik.errors.output}
                                    touched={chartQueryFormik.touched.output}
                                    customStyles={outputTypeSelectStyles}
                                    formatGroupLabel={(group) => (
                                        <div className="cs-filter-dropdown__group-label">
                                            {group.label}
                                        </div>
                                    )}
                                    formatOptionLabel={formatOutputOptionLabel}
                                    filterOption={createFilter({
                                        matchFrom: 'any',
                                        stringify: (candidate) => {
                                            const option = candidate.data as SelectFieldOption;
                                            return `${option.label} ${option.description || ''}`;
                                        },
                                    })}
                                    maxMenuHeight={outputTypeMaxMenuHeight}
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={true}
                                />
                            </Form.Group>
                        ) : null}

                    <ChartSettingsCompatibilityHintsPanel
                        chartType={chartSettingsFormik.values.type as ChartType}
                        chartQuery={chartQueryFormik.values}
                    />

                    {(chartSettingsFormik.values.type === ChartType.Wordcloud || chartSettingsFormik.values.type === ChartType.TrendingWords)
                        ? (
                            <>
                                <Form.Group className="cs-field" controlId="nGram" >
                                    <label className="cs-field__label">{i18n.t('n_gram.text')}</label>
                                    <SelectField
                                        compact
                                        name="n_gram"
                                        value={{
                                            value: chartQueryFormik.values.n_gram,
                                            label: `${chartQueryFormik.values.n_gram}${i18n.t('gram_suffix.text')}`,
                                        }}
                                        onChange={(v) => chartQueryFormik.setFieldValue('n_gram', v?.value || 1)}
                                        options={Array.from({length: 5}, (x, i) => ({
                                            value: i + 1,
                                            label: `${i + 1}${i18n.t('gram_suffix.text')}`,
                                        }))}
                                        isRequired={false}
                                        error={chartQueryFormik.errors.n_gram}
                                        touched={chartQueryFormik.touched.n_gram}
                                        customStyles={selectStyles}
                                        {...aggregationMenuProps}
                                    />
                                </Form.Group>
                                <Form.Group className="cs-field" controlId="wordcloudMostCommonWords" >
                                    <label className="cs-field__label">
                                        {i18n.t('jt_most_common.text')}
                                        <Tooltip
                                            content={i18n.t('jt_the_number_of.text')}
                                            placement="top"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ml-2"
                                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                                data-cy="chart-settings-btn-7">
                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                            </button>
                                        </Tooltip>
                                    </label>
                                    <Form.Control
                                        type="number"
                                        name="wordcloud_most_common_words"
                                        min={1}
                                        value={chartQueryFormik.values.wordcloud_most_common_words}
                                        onChange={chartQueryFormik.handleChange}
                                    />
                                </Form.Group>
                                <Form.Group className="cs-field" controlId="wordcloudTextStartPoint" >
                                    <label className="cs-field__label">
                                        {i18n.t('jt_text_start.text')}
                                        <Tooltip
                                            content={i18n.t('jt_the_number_of.text')}
                                            placement="top"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ml-2"
                                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                                data-cy="chart-settings-btn-6">
                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                            </button>
                                        </Tooltip>
                                    </label>
                                    <Form.Control
                                        type="number"
                                        name="wordcloud_text_start_point"
                                        value={chartQueryFormik.values.wordcloud_text_start_point}
                                        onChange={chartQueryFormik.handleChange}
                                    />
                                </Form.Group>
                                <Form.Group className="cs-field" controlId="wordcloudTextEndPoint" >
                                    <label className="cs-field__label">
                                        {i18n.t('jt_text_end.text')}
                                        <Tooltip
                                            content={i18n.t('jt_the_end.text')}
                                            placement="top"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ml-2"
                                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                                data-cy="chart-settings-btn-5">
                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                            </button>
                                        </Tooltip>
                                    </label>
                                    <Form.Control
                                        type="number"
                                        name="wordcloud_text_end_point"
                                        value={chartQueryFormik.values.wordcloud_text_end_point}
                                        onChange={chartQueryFormik.handleChange}
                                    />
                                </Form.Group>
                            </>
                        ) : null}


                    {chartSettingsFormik.values.type === ChartType.TrendingWords ? (
                        <Form.Group className="cs-field" controlId="trendingComparablePeriod">
                            <label className="cs-field__label"> {i18n.t('jt_comparable_period.text')}</label>
                            <SelectField
                                compact
                                name="trending_comparable_period"
                                value={{
                                    value: chartQueryFormik.values.trending_comparable_period,
                                    label: `${chartQueryFormik.values.trending_comparable_period} ${i18n.t('chart_settings_days.text')}`,
                                }}
                                onChange={(v) => chartQueryFormik.setFieldValue('trending_comparable_period', v?.value || 7)}
                                options={[
                                    {value: 7, label: i18n.t('chart_settings_7_days.text')},
                                    {value: 14, label: i18n.t('chart_settings_14_days.text')},
                                ]}
                                isRequired={false}
                                error={chartQueryFormik.errors.trending_comparable_period}
                                touched={chartQueryFormik.touched.trending_comparable_period}
                                customStyles={selectStyles}
                                {...aggregationMenuProps}
                            />
                        </Form.Group>) : null}


                    {chartSettingsFormik.values.type === ChartType.TrendingWords || chartSettingsFormik.values.type === ChartType.Wordcloud ? (
                        <Form.Group className="cs-field" controlId="filteringList">
                            <label className="cs-field__label">
                                {i18n.t('jt_select.text')}
                                <Tooltip
                                    content={i18n.t('jt_lexicons_view.text')}
                                    placement="top"
                                >
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 ml-2 chart-settings-lexicons-link"
                                        style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                        onClick={() => openLexiconsView()}
                                    >
                                        <Icon name="mdi-open-in-new" size="xs" className="mr-2" />
                                        {i18n.t('jt_lexicons.text')}
                                    </button>
                                </Tooltip>
                            </label>
                            <MultiSelectField
                                name="filtering_lists"
                                value={chartQueryFormik.values.filtering_lists || []}
                                onChange={(value) => chartQueryFormik.setFieldValue('filtering_lists', value || [])}
                                options={(wordcloudFilteringListsChoices ?? []).map((el) => ({
                                    value: el[0],
                                    label: el[1],
                                }))}
                                placeholder={i18n.t('jt_select.text')}
                                isRequired={false}
                                error={typeof chartQueryFormik.errors.filtering_lists === 'string' ? chartQueryFormik.errors.filtering_lists : undefined}
                                touched={!!chartQueryFormik.touched.filtering_lists}
                                isSearchable={true}
                                closeMenuOnSelect={false}
                                enableSelectAll={false}
                                {...aggregationMenuProps}
                            />
                        </Form.Group>
                    ) : null}

                    {chartSettingsFormik.values.type === ChartType.Concordance
                        ? (
                            <>
                                <Form.Group className="cs-field" controlId="concordanceQuery" >
                                    <label className="cs-field__label">
                                        {i18n.t('jt_concordance_keyword.text')}
                                        <Tooltip
                                            content={i18n.t('jt_target_word.text')}
                                            placement="top"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ml-2"
                                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                                data-cy="chart-settings-btn-3">
                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                            </button>
                                        </Tooltip>
                                    </label>
                                    <Form.Control
                                        onChange={chartQueryFormik.handleChange}
                                        name="concordance_query"
                                        value={chartQueryFormik.values.concordance_query}
                                        autoComplete="off"
                                    />
                                </Form.Group>
                                <Form.Group className="cs-field" controlId="concordanceWidth" >
                                    <label className="cs-field__label">
                                        {i18n.t('jt_concordance_width.text')}
                                        <Tooltip
                                            content={i18n.t('jt_the_width.text')}
                                            placement="top"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ml-2"
                                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                                data-cy="chart-settings-btn-2">
                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                            </button>
                                        </Tooltip>
                                    </label>
                                    <Form.Control
                                        type="number"
                                        name="concordance_width"
                                        min={10}
                                        value={chartQueryFormik.values.concordance_width}
                                        onChange={chartQueryFormik.handleChange}
                                    />
                                </Form.Group>
                                <Form.Group className="cs-field" controlId="concordanceLines" >
                                    <label className="cs-field__label">
                                        {i18n.t('jt_concordance_lines.text')}
                                        <Tooltip
                                            content={i18n.t('jt_the_max.text')}
                                            placement="top"
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 ml-2"
                                                style={{cursor: 'pointer', border: 'none', background: 'none'}}
                                                data-cy="chart-settings-btn-1">
                                                <Icon name="mdi-information-outline" size="xs" className='text-info' />
                                            </button>
                                        </Tooltip>
                                    </label>
                                    <Form.Control
                                        type="number"
                                        name="concordance_lines"
                                        min={1}
                                        value={chartQueryFormik.values.concordance_lines}
                                        onChange={chartQueryFormik.handleChange}
                                    />
                                </Form.Group>
                            </>
                        ) : null}

                    {[
                        ChartOutput.Terms,
                        ChartOutput.TermsTimeline,
                        ChartOutput.MetricTimeline,
                        ChartOutput.TermsMetric
                    ].some(output => output === chartQueryFormik.values.output)
                        && ![
                            ChartType.Wordcloud,
                            ChartType.TextSamples,
                            ChartType.Concordance,
                            ChartType.TrendingWords
                        ].includes(chartSettingsFormik.values.type)
                        ? (
                            <ChartAggregationsSelect
                                values={chartQueryFormik.values}
                                setFieldValue={chartQueryFormik.setFieldValue}
                                {...aggregationFieldProps}
                            />
                        ) : null}

                    {[
                        ChartOutput.MultiTerms,
                        ChartOutput.MultiTermsTimeline,
                    ].some(output => output === chartQueryFormik.values.output)
                        && ![
                            ChartType.Wordcloud,
                            ChartType.TextSamples,
                            ChartType.Concordance,
                            ChartType.TrendingWords
                        ].includes(chartSettingsFormik.values.type)
                        ? (
                            <>
                                <ChartAggregationsMultiSelect
                                    values={chartQueryFormik.values}
                                    setFieldValue={chartQueryFormik.setFieldValue}
                                    {...aggregationFieldProps}
                                />
                                {hierarchicalAggregationChartTypes.includes(chartSettingsFormik.values.type)
                                    && chartQueryFormik.values.output === ChartOutput.MultiTerms ? (
                                        <div className="mt-2">
                                            <div className="cs-helper-text">
                                                {chartSettingsFormik.values.type === ChartType.SunburstChart
                                                    ? i18n.t('sunburst_field_order_hint.text')
                                                    : i18n.t('hierarchical_field_order_hint.text')}
                                            </div>
                                            <Form.Check
                                                type="checkbox"
                                                id="nestedTermsIncludeMissingCheckbox"
                                                name="nested_terms_include_missing"
                                                label={i18n.t('nested_terms_include_missing.text')}
                                                checked={chartQueryFormik.values.nested_terms_include_missing}
                                                onChange={(event) => chartQueryFormik.setFieldValue(
                                                    'nested_terms_include_missing',
                                                    event.target.checked,
                                                )}
                                                className="cs-checkbox my-2"
                                            />
                                            <div className="cs-helper-text">
                                                {i18n.t('nested_terms_include_missing_hint.text')}
                                            </div>
                                        </div>
                                    ) : null}
                            </>
                        ) : null}

                    {(chartQueryFormik.values.output === ChartOutput.MetricTimeline)
                        && ![
                            ChartType.Wordcloud,
                            ChartType.TextSamples,
                            ChartType.Concordance,
                        ].includes(chartSettingsFormik.values.type)
                        ? (
                            <Form.Group className="cs-field" controlId="metricTimelineType" >
                                <label className="cs-field__label">{i18n.t('jt_metric_timeline.text')}</label>
                                <SelectField
                                    compact
                                    name="metric_timeline_type"
                                    value={chartMetricTimelineTypes.find(el => el[0] === chartQueryFormik.values.metric_timeline_type) ? {
                                        value: chartQueryFormik.values.metric_timeline_type,
                                        label: chartMetricTimelineTypes.find(el => el[0] === chartQueryFormik.values.metric_timeline_type)?.[1] || ''
                                    } : null}
                                    onChange={(v) => chartQueryFormik.setFieldValue('metric_timeline_type', v?.value || chartMetricTimelineTypes[0][0])}
                                    options={chartMetricTimelineTypes.map((el) => ({
                                        value: el[0],
                                        label: el[1],
                                    }))}
                                    isRequired={false}
                                    error={chartQueryFormik.errors.metric_timeline_type}
                                    touched={chartQueryFormik.touched.metric_timeline_type}
                                    customStyles={selectStyles}
                                    {...aggregationMenuProps}
                                />
                            </Form.Group>
                        ) : null}

                    {chartSettingsFormik.values.type === ChartType.PivotTable ? (
                        <>
                            <ChartAggregationsSelect
                                values={{
                                    ...chartQueryFormik.values,
                                    output: ChartOutput.PivotTerms,
                                }}
                                setFieldValue={chartQueryFormik.setFieldValue}
                                label={i18n.t('pivot_row_field.text')}
                                {...aggregationFieldProps}
                            />
                            <ChartPivotColumnSelect
                                values={chartQueryFormik.values}
                                setFieldValue={chartQueryFormik.setFieldValue}
                                {...aggregationFieldProps}
                            />
                            <Form.Group className="cs-field" controlId="pivotValueType">
                                <label className="cs-field__label">{i18n.t('pivot_value_type.text')}</label>
                                <SelectField
                                    compact
                                    name="metric_aggregations_type"
                                    value={{
                                        value: chartQueryFormik.values.metric_aggregations_type,
                                        label: pivotValueTypes.find(
                                            (entry) => entry[0] === chartQueryFormik.values.metric_aggregations_type,
                                        )?.[1] || '',
                                    }}
                                    onChange={(v) => chartQueryFormik.setFieldValue(
                                        'metric_aggregations_type',
                                        v?.value || AggregationType.Count,
                                    )}
                                    options={pivotValueTypes.map((entry) => ({
                                        value: entry[0],
                                        label: entry[1],
                                    }))}
                                    isRequired={false}
                                    error={chartQueryFormik.errors.metric_aggregations_type}
                                    touched={chartQueryFormik.touched.metric_aggregations_type}
                                    customStyles={selectStyles}
                                    {...aggregationMenuProps}
                                />
                            </Form.Group>
                            {chartQueryFormik.values.metric_aggregations_type !== AggregationType.Count ? (
                                <ChartAggregationsSelectCross
                                    values={chartQueryFormik.values}
                                    setFieldValue={chartQueryFormik.setFieldValue}
                                    {...aggregationFieldProps}
                                />
                            ) : null}
                        </>
                    ) : null}

                    {(((chartSettingsFormik.values.type === ChartType.Table || chartSettingsFormik.values.type === ChartType.BarChart)
                        && chartQueryFormik.values.output === ChartOutput.Terms))
                        ? (
                            <Form.Group className="cs-field" controlId="termsMetricType" >
                                <ChartAggregationsSelectCross
                                    values={chartQueryFormik.values}
                                    setFieldValue={chartQueryFormik.setFieldValue}
                                    {...aggregationFieldProps}
                                />
                            </Form.Group>
                        ) : null}

                    {showsMetricAggregationsTypeField(
                        chartSettingsFormik.values.type,
                        chartQueryFormik.values.output,
                        chartQueryFormik.values.metric_aggregations_aggs_item,
                    )
                        ? (
                            <Form.Group className="cs-field" controlId="termsMetricType" >
                                <label className="cs-field__label">{i18n.t('jt_metric_aggregations.text')}</label>
                                <SelectField
                                    compact
                                    name="metric_aggregations_type"
                                    value={{
                                        value: chartQueryFormik.values.metric_aggregations_type,
                                        label: chartSettingsFormik.values.type === ChartType.BigNumber
                                            ? numericMetricTimelineTypes.find((el) => el[0] === chartQueryFormik.values.metric_aggregations_type)?.[1] || ''
                                            : chartMetricTimelineTypes.find((el) => el[0] === chartQueryFormik.values.metric_aggregations_type)?.[1] || ''
                                    }}
                                    onChange={(v) => chartQueryFormik.setFieldValue('metric_aggregations_type', v?.value || (chartSettingsFormik.values.type === ChartType.BigNumber ? numericMetricTimelineTypes[0][0] : chartMetricTimelineTypes[0][0]))}
                                    options={(chartSettingsFormik.values.type === ChartType.BigNumber
                                        || ((chartSettingsFormik.values.type === ChartType.Table || chartSettingsFormik.values.type === ChartType.BarChart)
                                            && chartQueryFormik.values.output === ChartOutput.Terms))
                                        ? numericMetricTimelineTypes.map(el => ({
                                            value: el[0],
                                            label: el[1],
                                        }))
                                        : chartMetricTimelineTypes.map(el => ({
                                            value: el[0],
                                            label: el[1],
                                        }))
                                    }
                                    isRequired={false}
                                    error={chartQueryFormik.errors.metric_aggregations_type}
                                    touched={chartQueryFormik.touched.metric_aggregations_type}
                                    customStyles={selectStyles}
                                    {...aggregationMenuProps}
                                />
                            </Form.Group>
                        ) : null}

                    {![
                        ChartType.Wordcloud,
                        ChartType.TextSamples,
                        ChartType.Concordance,
                    ].includes(chartSettingsFormik.values.type)
                        && showsMetricIntervalField(chartQueryFormik.values)
                        ? (
                            <Form.Group className="cs-field" controlId="metricInterval" >
                                <label className="cs-field__label">{i18n.t('jt_interval.text')}</label>
                                <Form.Control
                                    type="number"
                                    name="metric_interval"
                                    min={1}
                                    value={chartQueryFormik.values.metric_interval}
                                    onChange={chartQueryFormik.handleChange}
                                    isValid={chartQueryFormik.touched.metric_interval && !chartQueryFormik.errors.metric_interval}
                                />
                            </Form.Group>
                        ) : null}
                        </ChartSettingsPanel>
        </div>
    );
}, (prevProps, nextProps) => (
    prevProps.onChartTypeChange === nextProps.onChartTypeChange
    && prevProps.eventFilters === nextProps.eventFilters
    && prevProps.eventFiltersLoading === nextProps.eventFiltersLoading
    && prevProps.userData === nextProps.userData
    && prevProps.wordcloudFilteringListsChoices === nextProps.wordcloudFilteringListsChoices
    && isEqual(
        chartQueryWithoutSearchText(prevProps.chartQueryFormik.values),
        chartQueryWithoutSearchText(nextProps.chartQueryFormik.values),
    )
    && isEqual(
        chartSettingsWithoutPreviewText(prevProps.chartSettingsFormik.values),
        chartSettingsWithoutPreviewText(nextProps.chartSettingsFormik.values),
    )
    && prevProps.chartQueryFormik.errors === nextProps.chartQueryFormik.errors
    && prevProps.chartSettingsFormik.errors === nextProps.chartSettingsFormik.errors
));

export default ChartSettingsVisualizationPanel;

